import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'baseui/snackbar'
import { showSnackbar, SNACKBAR_TYPES } from '@/shared/tools/notifications/notification'
import { fidelityMaximizerModuleActions, PENDING_REGISTRATION_SCENARIO } from './FidelityMaximizerSagas'
import {
  selectConfig,
  selectContactConfig,
  selectFidelityMaximizerState,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import { updateFidelityMaximizer } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSlice'
import useFidelityMaximizerEligibility from './useFidelityMaximizerEligibility'
import useFidelityActionRealtimeSync from './useFidelityActionRealtimeSync'
import useSecondsUntil from '@/shared/hooks/useSecondsUntil'
import { formatFrenchSentAt, formatFrenchTime } from './formatFrenchDateTime'
import { clamp } from '@/shared/tools/dateTime'

const MODULE = 'subscriptionForm:fidelity_maximizer_module'
const CODE_COPY = `${MODULE}.code_onboarding`

const HANDOVER_ACTION = 'myselectra_registration'
const REGISTRATION_CODE_ACTION = 'myselectra_registration_code'
const NLBP_ACTIVATION_NOTIFICATION = 'nlbp-account-activation'

const RESEND_COOLDOWN_SECONDS = 30
const REGENERATE_COOLDOWN_SECONDS = 10
const PHASES = { SEND: 'send', CODE: 'code', DICTATION: 'dictation' }
const STEP_KEYS = [PHASES.SEND, PHASES.CODE, PHASES.DICTATION]
const NO_CODE_OUTCOMES = ['nlbp_activation', 'account_exists']

const SCENARIO_COPY_KEYS = {
  nothing: 'scenario_1',
  nlbp_classic_only: 'scenario_1',
  nlbp_personalized_only: 'scenario_3',
  myselectra_no_app: 'scenario_4',
}

const MYSELECTRA_COPY_KEYS = ['scenario_1_with_pdl', 'scenario_3']

export const resolveLegacyCopyKey = (scenarioKey, isPdlEligible) => {
  const baseCopyKey = SCENARIO_COPY_KEYS[scenarioKey ?? '']

  if (!baseCopyKey) {
    return null
  }

  if (baseCopyKey !== 'scenario_1') {
    return baseCopyKey
  }

  return isPdlEligible ? 'scenario_1_with_pdl' : 'scenario_1_without_pdl'
}

// Pending hands back the handover key as a resend; every other scenario hands back the legacy newsletter key.
const resolveLegacyActionKey = ({ isPendingRegistration, secondaryActionKey, codeCardVisible, primaryActionKey }) => {
  if (isPendingRegistration) {
    return null
  }

  if (secondaryActionKey) {
    return secondaryActionKey
  }

  return codeCardVisible ? null : primaryActionKey
}

// The same broadcast carries legacy newsletter actions, whose payload never has a pending_user.
export const resolveActionOutcome = actionResult => {
  if (!actionResult || actionResult.actionKey !== HANDOVER_ACTION) {
    return null
  }

  const { success, message, data } = actionResult

  if (!success) {
    return { kind: 'error', message }
  }

  if (data?.notification === NLBP_ACTIVATION_NOTIFICATION) {
    return { kind: 'nlbp_activation', message }
  }

  return { kind: data?.pending_user ? 'pending' : 'account_exists', message }
}

const useFidelityMaximizer = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { enqueue } = useSnackbar()
  const { contactId } = useSelector(selectConfig)
  const { contractLoaded } = useSelector(selectContactConfig)
  const fidelityMaximizer = useSelector(selectFidelityMaximizerState)
  const {
    loading,
    loaded,
    enabled,
    isPdlEligible,
    missingContactFields = [],
    scenarioKey,
    actionKey,
    actionAvailable,
    actionKeys = null,
    pendingRegistration = null,
    actionLoading,
    actionResult = null,
    codeLoading = false,
    registrationCode = null,
  } = fidelityMaximizer

  const isEligibleAgent = useFidelityMaximizerEligibility()

  // An empty array is still an array: treating it as the new flow would leave the module with no card at all.
  const codeOnboardingEnabled = Array.isArray(actionKeys) && actionKeys.length > 0
  const primaryActionKey = codeOnboardingEnabled ? (actionKeys[0] ?? null) : actionKey
  const secondaryActionKey = codeOnboardingEnabled ? (actionKeys[1] ?? null) : null
  const isPendingRegistration = scenarioKey === PENDING_REGISTRATION_SCENARIO
  const handoverProposed = primaryActionKey === HANDOVER_ACTION
  const codeCardVisible = codeOnboardingEnabled && (handoverProposed || isPendingRegistration)

  const resendActionKey = isPendingRegistration ? secondaryActionKey : null
  const legacyActionKey = resolveLegacyActionKey({
    isPendingRegistration,
    secondaryActionKey,
    codeCardVisible,
    primaryActionKey,
  })
  const legacyCopyKey = resolveLegacyCopyKey(scenarioKey, isPdlEligible)

  const contactCode = registrationCode?.contactId === contactId ? registrationCode : null
  const phase = contactCode?.code ? PHASES.DICTATION : isPendingRegistration ? PHASES.CODE : PHASES.SEND

  const outcome = actionResult?.contactId === contactId ? resolveActionOutcome(actionResult) : null

  // The action broadcast clears actionLoading, but the phase only moves once the status reload lands.
  // Without this the agent can send the handover email a second time in between.
  const handoverAwaitingStatus = phase === PHASES.SEND && outcome?.kind === 'pending'

  const handedOverTime = pendingRegistration?.handed_over_at
    ? new Date(pendingRegistration.handed_over_at).getTime()
    : NaN

  // handed_over_at comes from the server clock, so anchor the deadline locally once and cap it at the
  // cooldown: a workstation running behind the server must not be able to stretch the wait.
  const resendDeadline = useMemo(() => {
    if (Number.isNaN(handedOverTime)) {
      return null
    }

    const window = RESEND_COOLDOWN_SECONDS * 1000
    return new Date(Date.now() + clamp(handedOverTime + window - Date.now(), 0, window)).toISOString()
  }, [handedOverTime])

  const resendSeconds = useSecondsUntil(resendDeadline)
  const regenerateSeconds = useSecondsUntil(contactCode?.issuedAt, REGENERATE_COOLDOWN_SECONDS)
  const codeSeconds = useSecondsUntil(contactCode?.expiresAt)
  const contactIncomplete = missingContactFields.length > 0

  const inFlightAction = useRef(null)

  // A regenerated code can repeat, so the progress window is measured from its expiry rather than from the code.
  const codeValidity = useRef({ expiresAt: undefined, total: 0 })

  if (codeValidity.current.expiresAt !== contactCode?.expiresAt) {
    codeValidity.current = { expiresAt: contactCode?.expiresAt, total: Math.max(codeSeconds, 1) }
  }

  const handleActionCompleted = useCallback(
    ({ success, message, data }) => {
      const actionResult = { contactId, actionKey: inFlightAction.current, success, message, data }
      dispatch(updateFidelityMaximizer({ actionLoading: false, actionResult }))
      showSnackbar(enqueue, {
        type: success ? SNACKBAR_TYPES.SUCCESS : SNACKBAR_TYPES.ERROR,
        message: message || (success ? 'Action envoyée avec succès.' : "Erreur lors de l'envoi."),
      })
    },
    [dispatch, enqueue, contactId],
  )

  useFidelityActionRealtimeSync(contactId, isEligibleAgent, handleActionCompleted)

  useEffect(() => {
    if (!contractLoaded || !contactId || !isEligibleAgent || loaded || loading) {
      return
    }

    dispatch({ type: fidelityMaximizerModuleActions.FIDELITY_MAXIMIZER_LOAD_STATUS, contactId })
  }, [contractLoaded, contactId, isEligibleAgent, loaded, loading])

  const dispatchAction = action => {
    inFlightAction.current = action
    dispatch({
      type: fidelityMaximizerModuleActions.FIDELITY_MAXIMIZER_TRIGGER_ACTION,
      contactId,
      action,
      enqueue,
    })
  }

  const moduleBlocked = !actionAvailable || contactIncomplete
  const actionBlocked = moduleBlocked || actionLoading
  const codeRequestBlocked = moduleBlocked || codeLoading

  const triggerAction = () => {
    const isSendable = primaryActionKey && primaryActionKey !== REGISTRATION_CODE_ACTION
    const cannotSend = !isSendable || actionBlocked || handoverAwaitingStatus

    if (cannotSend) {
      return
    }

    dispatchAction(primaryActionKey)
  }

  const triggerLegacyAction = () => {
    const cannotSendLegacy = !legacyActionKey || actionBlocked

    if (cannotSendLegacy) {
      return
    }

    dispatchAction(legacyActionKey)
  }

  const resendHandover = () => {
    const cannotResend = !resendActionKey || actionBlocked || resendSeconds > 0

    if (cannotResend) {
      return
    }

    dispatchAction(resendActionKey)
  }

  const requestRegistrationCode = () => {
    const cannotRequestCode = !isPendingRegistration || codeRequestBlocked || regenerateSeconds > 0

    if (cannotRequestCode) {
      return
    }

    dispatch({
      type: fidelityMaximizerModuleActions.FIDELITY_MAXIMIZER_ISSUE_REGISTRATION_CODE,
      contactId,
      enqueue,
    })
  }

  const isFullyLoyal = scenarioKey === 'fully_loyal'
  const codeCardHidden = !actionAvailable || !codeCardVisible
  const legacyCardHidden = !actionAvailable || !legacyActionKey || !legacyCopyKey
  const proposesMyselectra = !codeCardHidden || MYSELECTRA_COPY_KEYS.includes(legacyCopyKey ?? '')

  const phaseIndex = STEP_KEYS.indexOf(phase)
  const steps = STEP_KEYS.map((key, index) => ({
    key,
    label: t(`${CODE_COPY}.steps.${key}`),
    done: index < phaseIndex,
    current: index === phaseIndex,
  }))

  const noCodeExpected = Boolean(outcome && NO_CODE_OUTCOMES.includes(outcome.kind))
  const spelledCode = contactCode?.code ? contactCode.code.split('').join(' ') : ''
  const remainingLabel =
    codeSeconds >= 60
      ? t(`${CODE_COPY}.remaining_minutes`, { minutes: Math.floor(codeSeconds / 60), seconds: codeSeconds % 60 })
      : t(`${CODE_COPY}.remaining_seconds`, { seconds: codeSeconds })

  return {
    t,
    hidden: !isEligibleAgent || !loaded || !enabled,
    phase,
    missingContactFields,
    registrationCode: contactCode?.code ? { code: contactCode.code, expiresAt: contactCode.expiresAt ?? null } : null,
    outcome,
    resendSeconds,
    regenerateSeconds,
    legacyActionKey,
    codeOnboardingEnabled,
    triggerAction,
    triggerLegacyAction,
    resendHandover,
    requestRegistrationCode,
    loyaltyBannerHidden: !isFullyLoyal,
    pdlWarningHidden: isFullyLoyal || isPdlEligible,
    pdlOkBannerHidden: isFullyLoyal || !isPdlEligible,
    pdlOkBannerKey: proposesMyselectra ? 'pdl_ok_banner_propose_myselectra' : 'pdl_ok_banner',
    orDividerHidden: codeCardHidden || legacyCardHidden,
    contactWarning: {
      hidden: !contactIncomplete || (codeCardHidden && legacyCardHidden),
      text: t(`${MODULE}.contact_incomplete_warning`, {
        fields: missingContactFields
          .map(field => t(`${MODULE}.contact_fields.${field}`, { defaultValue: field }))
          .join(', '),
      }),
    },
    codeCard: {
      hidden: codeCardHidden,
      steps,
      sentBox: {
        hidden: phase === PHASES.SEND || !pendingRegistration,
        title: t(`${CODE_COPY}.code.sent_at`, {
          time: pendingRegistration ? formatFrenchSentAt(pendingRegistration.handed_over_at) : null,
        }),
        details: [
          pendingRegistration?.notification &&
            t(`${CODE_COPY}.notification_labels.${pendingRegistration.notification}`, { defaultValue: '' }),
          pendingRegistration?.newsletter &&
            t(`${CODE_COPY}.newsletter_labels.${pendingRegistration.newsletter}`, { defaultValue: '' }),
        ]
          .filter(Boolean)
          .join(' · '),
      },
      script: t(`${CODE_COPY}.${phase}.script`, { code: spelledCode }),
      info: t(`${CODE_COPY}.${phase}.info`),
      code: {
        hidden: phase !== PHASES.DICTATION,
        code: contactCode?.code ?? '',
        expiryLabel:
          codeSeconds > 0
            ? t(`${CODE_COPY}.dictation.expires_at`, {
                time: formatFrenchTime(contactCode?.expiresAt),
                remaining: remainingLabel,
              })
            : t(`${CODE_COPY}.dictation.expired`),
        progress: { value: codeSeconds, maxValue: codeValidity.current.total },
      },
      outcome: {
        hidden: !outcome || outcome.kind === 'pending',
        type: outcome?.kind === 'error' ? 'error' : 'info',
        message: outcome?.message ?? '',
        detail: noCodeExpected ? t(`${CODE_COPY}.outcomes.${outcome.kind}`) : '',
      },
      primaryAction: {
        hidden: noCodeExpected,
        label:
          regenerateSeconds > 0
            ? t(`${CODE_COPY}.dictation.cta_countdown`, { seconds: regenerateSeconds })
            : t(`${CODE_COPY}.${phase}.cta`),
        disabled: contactIncomplete || handoverAwaitingStatus || regenerateSeconds > 0,
        loading: actionLoading || codeLoading || loading,
        onClick: phase === PHASES.SEND ? triggerAction : requestRegistrationCode,
      },
      resendAction: {
        hidden: !resendActionKey || phase !== PHASES.CODE,
        label:
          resendSeconds > 0
            ? t(`${CODE_COPY}.code.resend_countdown`, { seconds: resendSeconds })
            : t(`${CODE_COPY}.code.resend`),
        disabled: resendSeconds > 0,
        loading: actionLoading,
        onClick: resendHandover,
      },
    },
    legacyCard: {
      hidden: legacyCardHidden,
      copyKey: legacyCopyKey ?? '',
      demoted: !codeCardHidden,
      disabled: contactIncomplete,
      loading: actionLoading,
      onClick: triggerLegacyAction,
    },
  }
}

export default useFidelityMaximizer
